import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import apiService from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import { formatLargeNumber } from '../../utils/helpers';
import './AggregateDashboard.css';

const STAGE_COLORS = {
  a1: '#1f3c88',
  a2: '#2f5d90',
  a3: '#3a78b7',
  a4_a5: '#4c956c',
  b2_b5: '#6aa889',
  c1_c4: '#94b3d2'
};

const AXIS_TICK_STYLE = {
  fill: '#1f2d3d',
  fontSize: 12,
  fontWeight: 500
};

const LEGEND_TEXT_STYLE = {
  color: '#1f2d3d',
  fontSize: 12,
  fontWeight: 500
};

const PIE_CHART_HEIGHT = 450;
const PIE_CHART_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
const BAR_CHART_MARGIN = { top: 110, right: 30, bottom: 60, left: 60 };
const BAR_LEGEND_WRAPPER_STYLE = { ...LEGEND_TEXT_STYLE, paddingTop: 12 };
const PIE_TOOLTIP_WRAPPER_STYLE = {
  padding: 12,
  backgroundColor: '#ffffff',
  border: '1px solid #d9e2ef',
  borderRadius: 8,
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)'
};

const AggregateDashboard = () => {
  const [availableScenarios, setAvailableScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState([]);
  const [aggregateData, setAggregateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterFacility, setFilterFacility] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadAvailableScenarios();
  }, []);

  useEffect(() => {
    if (!showScenarioDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowScenarioDropdown(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowScenarioDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showScenarioDropdown]);

  const loadAvailableScenarios = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/aggregate/available-scenarios');
      setAvailableScenarios(response.data.scenarios || []);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
      setError('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleScenarioToggle = (scenarioId) => {
    setSelectedScenarios((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      }
      return [...prev, scenarioId];
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredScenarios();
    const allIds = filtered.map((s) => s.id);
    setSelectedScenarios(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedScenarios([]);
  };

  const handleAnalyze = async () => {
    if (selectedScenarios.length === 0) {
      setError('Please select at least one scenario');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setShowScenarioDropdown(false);
      const response = await apiService.post('/aggregate/analyze', {
        scenario_ids: selectedScenarios
      });
      setAggregateData(response.data);
    } catch (err) {
      console.error('Failed to analyze scenarios:', err);
      setError(err.response?.data?.detail || 'Failed to analyze scenarios');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredScenarios = () => {
    let filtered = [...availableScenarios];

    if (filterFacility !== 'all') {
      filtered = filtered.filter((s) => s.facility === filterFacility);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'facility':
          return a.facility.localeCompare(b.facility);
        case 'emissions':
          return (b.total_emissions || 0) - (a.total_emissions || 0);
        case 'created_at':
        default:
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return filtered;
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

  const formatEmissionValue = (value, decimals = 2) =>
    formatLargeNumber(value || 0, { decimals });

  const preparePlantComparisonData = () => {
    if (!aggregateData) return [];

    return aggregateData.plants.map((plant) => ({
      facility: plant.facility,
      total: plant.total_emissions,
      a1: plant.total_a1,
      a2: plant.total_a2,
      a3: plant.total_a3,
      a4_a5: plant.total_a4_a5,
      b2_b5: plant.total_b2_b5,
      c1_c4: plant.total_c1_c4,
      avg: plant.avg_emissions
    }));
  };

  const prepareStageDistributionData = () => {
    if (!aggregateData) return [];

    return [
      { name: 'A1 - Quarry', value: aggregateData.grand_total_a1, color: STAGE_COLORS.a1 },
      { name: 'A2 - Transport', value: aggregateData.grand_total_a2, color: STAGE_COLORS.a2 },
      { name: 'A3 - Manufacturing', value: aggregateData.grand_total_a3, color: STAGE_COLORS.a3 },
      { name: 'A4-A5 - Installation', value: aggregateData.grand_total_a4_a5, color: STAGE_COLORS.a4_a5 },
      { name: 'B2-B5 - Maintenance', value: aggregateData.grand_total_b2_b5, color: STAGE_COLORS.b2_b5 },
      { name: 'C1-C4 - End of Life', value: aggregateData.grand_total_c1_c4, color: STAGE_COLORS.c1_c4 }
    ].filter((item) => item.value > 0);
  };

  const renderCustomLegend = (props) => {
    const { payload } = props;
    if (!payload || !payload.length) return null;

    const stageLegendTotal = payload.reduce((sum, entry) => sum + (entry.payload?.value || 0), 0);

    return (
      <div className="custom-pie-legend">
        {payload.map((entry, index) => {
          const percentage = stageLegendTotal
            ? ((entry.payload.value / stageLegendTotal) * 100).toFixed(1)
            : '0.0';
          
          return (
            <div key={`legend-${index}`} className="legend-item">
              <div className="legend-marker" style={{ backgroundColor: entry.color }} />
              <div className="legend-content">
                <div className="legend-label">{entry.value}</div>
                <div className="legend-value">
                  {formatEmissionValue(entry.payload.value)} kg CO₂e ({percentage}%)
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const filteredScenarios = getFilteredScenarios();
  const selectedScenarioDetails = availableScenarios.filter((scenario) =>
    selectedScenarios.includes(scenario.id)
  );

  const analysisDisabled = selectedScenarios.length === 0 || loading;
  const stageDistributionData = prepareStageDistributionData();
  const stageLegendTotal = stageDistributionData.reduce(
    (sum, item) => sum + (item.value || 0),
    0
  );
  const plantComparisonData = preparePlantComparisonData();

  if (loading && !aggregateData && availableScenarios.length === 0) {
    return <LoadingSpinner message="Loading scenarios..." />;
  }

  return (
    <div className="aggregate-dashboard">
      <div className="aggregate-header">
        <h1>Comparative Analysis Dashboard</h1>
        <p>Select multiple scenarios to compare emissions across plants and stages</p>
      </div>

      {error && (
        <div className="error-banner" role="alert">
          <span className="error-icon" aria-hidden />
          {error}
        </div>
      )}

      <div className="aggregate-content">
        <div className="scenario-toolbar">
          <div className="toolbar-filters">
            <div className="filter-group flex-1">
              <label htmlFor="scenario-selector">Scenarios</label>
              <div className="selector-control" ref={dropdownRef}>
                <button
                  type="button"
                  id="scenario-selector"
                  className="selector-trigger"
                  onClick={() => setShowScenarioDropdown((prev) => !prev)}
                  aria-haspopup="listbox"
                  aria-expanded={showScenarioDropdown}
                >
                  <span>
                    {selectedScenarios.length > 0
                      ? `${selectedScenarios.length} selected`
                      : 'Select scenarios'}
                  </span>
                  <span className="selector-caret" aria-hidden>
                    ▾
                  </span>
                </button>

                {showScenarioDropdown && (
                  <div className="selector-dropdown" role="listbox">
                    <div className="selector-header">
                      <span>{filteredScenarios.length} scenarios</span>
                      <div className="selector-actions">
                        <button type="button" onClick={handleSelectAll}>
                          Select All
                        </button>
                        <button type="button" onClick={handleDeselectAll}>
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="selector-list">
                      {filteredScenarios.map((scenario) => (
                        <label key={scenario.id} className="selector-option">
                          <input
                            type="checkbox"
                            checked={selectedScenarios.includes(scenario.id)}
                            onChange={() => handleScenarioToggle(scenario.id)}
                          />
                          <div className="option-details">
                            <span className="option-name">{scenario.name}</span>
                            <span className="option-meta">
                              {scenario.facility}
                              {scenario.created_at ? ` · ${formatDate(scenario.created_at)}` : ''}
                            </span>
                            {scenario.has_emissions && (
                              <span className="option-emissions">
                                {formatEmissionValue(scenario.total_emissions)} kg CO₂e
                              </span>
                            )}
                          </div>
                        </label>
                      ))}

                      {filteredScenarios.length === 0 && (
                        <div className="selector-empty">No scenarios found for the selected filters</div>
                      )}
                    </div>

                    <div className="selector-footer">
                      <button type="button" onClick={() => setShowScenarioDropdown(false)}>
                        Done
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group flex-1">
              <label htmlFor="facility-filter">Facility</label>
              <select
                id="facility-filter"
                value={filterFacility}
                onChange={(e) => setFilterFacility(e.target.value)}
              >
                <option value="all">All Facilities</option>
                {[...new Set(availableScenarios.map((s) => s.facility))]
                  .filter(Boolean)
                  .sort()
                  .map((facility) => (
                    <option key={facility} value={facility}>
                      {facility}
                    </option>
                  ))}
              </select>
            </div>

            <div className="filter-group flex-1">
              <label htmlFor="sort-by">Sort by</label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="created_at">Date Created</option>
                <option value="name">Name</option>
                <option value="facility">Facility</option>
                <option value="emissions">Emissions</option>
              </select>
            </div>

            <div className="filter-group flex-1">
              <label>&nbsp;</label>
              <button
                type="button"
                className="btn-primary analyze-button"
                onClick={handleAnalyze}
                disabled={analysisDisabled}
              >
                {loading ? 'Analyzing…' : 'Analyze'}
              </button>
            </div>
          </div>
        </div>

        {selectedScenarioDetails.length > 0 && (
          <>
            <div className="selected-scenarios" role="list">
              {selectedScenarioDetails.map((scenario) => (
                <div key={scenario.id} className="scenario-chip" role="listitem">
                  <div className="chip-text">
                    <span className="chip-name">{scenario.name}</span>
                    <span className="chip-meta">{scenario.facility}</span>
                  </div>
                  <button
                    type="button"
                    className="chip-remove"
                    aria-label={`Remove ${scenario.name}`}
                    onClick={() => handleScenarioToggle(scenario.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="scenario-breakdown-grid">
              {selectedScenarioDetails.map((scenario) => (
                <div key={`${scenario.id}-card`} className="scenario-breakdown-card">
                  <div className="scenario-card-header">
                    <h3>{scenario.name}</h3>
                    {scenario.created_at && <span>{formatDate(scenario.created_at)}</span>}
                  </div>
                  <div className="scenario-card-meta">
                    <span className="facility-badge">{scenario.facility}</span>
                    {scenario.has_emissions && (
                      <span className="scenario-emissions">
                        {formatEmissionValue(scenario.total_emissions)} kg CO₂e
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="results-wrapper">
          {!aggregateData ? (
            <div className="results-panel empty-results">
              <div className="empty-icon" aria-hidden />
              <h3>No Analysis Yet</h3>
              <p>Select scenarios from the dropdown above and click "Analyze" to see aggregate results.</p>
            </div>
          ) : (
            <div className="results-panel">
              <div className="summary-cards">
                <div className="summary-card grand-total">
                  <div className="card-icon accent-primary" aria-hidden>
                    GT
                  </div>
                  <div className="card-content">
                    <div className="card-label">Grand Total Emissions</div>
                    <div className="card-value">
                      {formatEmissionValue(aggregateData.grand_total_emissions)}
                      <span className="unit">kg CO₂e</span>
                    </div>
                  </div>
                </div>

                <div className="summary-card">
                  <div className="card-icon accent-secondary" aria-hidden>
                    SA
                  </div>
                  <div className="card-content">
                    <div className="card-label">Scenarios Analyzed</div>
                    <div className="card-value">{aggregateData.total_scenarios}</div>
                  </div>
                </div>

                <div className="summary-card">
                  <div className="card-icon accent-tertiary" aria-hidden>
                    PI
                  </div>
                  <div className="card-content">
                    <div className="card-label">Plants Included</div>
                    <div className="card-value">{aggregateData.plants.length}</div>
                  </div>
                </div>

                {/* <div className="summary-card">
                  <div className="card-icon accent-quaternary" aria-hidden>
                    AS
                  </div>
                  <div className="card-content">
                    <div className="card-label">Average per Scenario</div>
                    <div className="card-value">
                      {formatEmissionValue(aggregateData.avg_emissions_per_scenario)}
                      <span className="unit">kg CO₂e</span>
                    </div>
                  </div>
                </div> */}
              </div>

              <div className="charts-grid">
                <div className="chart-container pie-chart-container">
                  <h3>Overall Stage Distribution</h3>
                  <div className="pie-chart-flex-wrapper">
                    <div className="pie-chart-visual">
                      <ResponsiveContainer width="100%" height={PIE_CHART_HEIGHT}>
                        <PieChart>
                          <Pie
                            data={stageDistributionData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={130}
                            innerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {stageDistributionData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip
                            wrapperStyle={PIE_TOOLTIP_WRAPPER_STYLE}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const dataPoint = payload[0].payload;
                              const percentage = stageLegendTotal
                                ? ((dataPoint.value / stageLegendTotal) * 100).toFixed(2)
                                : '0.00';

                              return (
                                <div className="tooltip-card">
                                  <div className="tooltip-title">{dataPoint.name}</div>
                                  <div className="tooltip-value">
                                    {formatEmissionValue(dataPoint.value)}
                                    <span className="tooltip-unit">kg CO₂e</span>
                                  </div>
                                  <div className="tooltip-subtext">{percentage}% of total</div>
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="stage-data-table">
                      <h4>Emissions by Stage</h4>
                      <table className="stage-table">
                        <thead>
                          <tr>
                            <th>Stage</th>
                            <th>Emissions</th>
                            <th>%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stageDistributionData.map((stage, index) => {
                            const percentage = stageLegendTotal
                              ? ((stage.value / stageLegendTotal) * 100).toFixed(1)
                              : '0.0';
                            return (
                              <tr key={index}>
                                <td>
                                  <div className="stage-name-cell">
                                    <span className="stage-color-dot" style={{ backgroundColor: stage.color }}></span>
                                    <span>{stage.name}</span>
                                  </div>
                                </td>
                                <td className="value-cell">{formatEmissionValue(stage.value)} kg CO₂e</td>
                                <td className="percent-cell">{percentage}%</td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="total-row">
                            <td><strong>Total</strong></td>
                            <td className="value-cell"><strong>{formatEmissionValue(stageLegendTotal)} kg CO₂e</strong></td>
                            <td className="percent-cell"><strong>100.0%</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="chart-container bar-chart-container wide">
                  <h3>Plant-by-Plant Comparison</h3>
                  <div className="plant-comparison-layout">
                    <div className="comparison-chart-wrapper">
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={plantComparisonData} margin={BAR_CHART_MARGIN}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#d9e2ef" />
                          <XAxis
                            dataKey="facility"
                            angle={-25}
                            textAnchor="end"
                            height={80}
                            tick={AXIS_TICK_STYLE}
                            tickLine={false}
                            tickMargin={8}
                            interval={0}
                          />
                          <YAxis
                            label={{ 
                              value: 'Total Emissions (kg CO₂e)', 
                              angle: -90, 
                              position: 'insideLeft', 
                              fill: '#1f2d3d',
                              style: { fontSize: 13, fontWeight: 600 }
                            }}
                            tickFormatter={(value) => formatEmissionValue(value, 0)}
                            tick={AXIS_TICK_STYLE}
                            tickLine={false}
                            width={80}
                          />
                          <Tooltip
                            wrapperStyle={PIE_TOOLTIP_WRAPPER_STYLE}
                            content={({ active, payload }) => {
                              if (!active || !payload?.length) return null;
                              const data = payload[0].payload;
                              return (
                                <div className="tooltip-card">
                                  <div className="tooltip-title">{data.facility}</div>
                                  <div className="tooltip-value">
                                    {formatEmissionValue(data.total)}
                                    <span className="tooltip-unit">kg CO₂e Total</span>
                                  </div>
                                  <div className="tooltip-breakdown">
                                    <div>A1: {formatEmissionValue(data.a1)}</div>
                                    <div>A2: {formatEmissionValue(data.a2)}</div>
                                    <div>A3: {formatEmissionValue(data.a3)}</div>
                                    <div>A4-A5: {formatEmissionValue(data.a4_a5)}</div>
                                    <div>B2-B5: {formatEmissionValue(data.b2_b5)}</div>
                                    <div>C1-C4: {formatEmissionValue(data.c1_c4)}</div>
                                  </div>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="total" fill="#1f3c88" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    
                    <div className="plant-comparison-cards">
                      {plantComparisonData.map((plant, index) => (
                        <div key={index} className="plant-card">
                          <div className="plant-card-header">
                            <span className="plant-name">{plant.facility}</span>
                            <span className="plant-total">{formatEmissionValue(plant.total)} kg CO₂e</span>
                          </div>
                          <div className="plant-stage-bars">
                            {[
                              { key: 'a1', label: 'A1', color: STAGE_COLORS.a1, value: plant.a1 },
                              { key: 'a2', label: 'A2', color: STAGE_COLORS.a2, value: plant.a2 },
                              { key: 'a3', label: 'A3', color: STAGE_COLORS.a3, value: plant.a3 },
                              { key: 'a4_a5', label: 'A4-A5', color: STAGE_COLORS.a4_a5, value: plant.a4_a5 },
                              { key: 'b2_b5', label: 'B2-B5', color: STAGE_COLORS.b2_b5, value: plant.b2_b5 },
                              { key: 'c1_c4', label: 'C1-C4', color: STAGE_COLORS.c1_c4, value: plant.c1_c4 }
                            ].map((stage) => {
                              const percentage = plant.total > 0 ? (stage.value / plant.total) * 100 : 0;
                              return (
                                <div key={stage.key} className="stage-bar-row">
                                  <div className="stage-bar-label">{stage.label}</div>
                                  <div className="stage-bar-container">
                                    <div 
                                      className="stage-bar-fill" 
                                      style={{ 
                                        width: `${percentage}%`, 
                                        backgroundColor: stage.color 
                                      }}
                                    />
                                  </div>
                                  <div className="stage-bar-value">{formatEmissionValue(stage.value)}</div>
                                  <div className="stage-bar-percent">{percentage.toFixed(1)}%</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="plant-details-table">
                <h3>Detailed Breakdown by Plant</h3>
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Facility</th>
                        <th>Scenarios</th>
                        <th>Total Emissions</th>
                        <th>A1</th>
                        <th>A2</th>
                        <th>A3</th>
                        <th>A4-A5</th>
                        <th>B2-B5</th>
                        <th>C1-C4</th>
                        <th>Avg/Scenario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aggregateData.plants.map((plant, index) => (
                        <tr key={index}>
                          <td className="facility-cell">{plant.facility}</td>
                          <td className="text-center">{plant.scenario_count}</td>
                          <td className="emissions-cell">
                            {formatEmissionValue(plant.total_emissions)}
                          </td>
                          <td>{formatEmissionValue(plant.total_a1)}</td>
                          <td>{formatEmissionValue(plant.total_a2)}</td>
                          <td>{formatEmissionValue(plant.total_a3)}</td>
                          <td>{formatEmissionValue(plant.total_a4_a5)}</td>
                          <td>{formatEmissionValue(plant.total_b2_b5)}</td>
                          <td>{formatEmissionValue(plant.total_c1_c4)}</td>
                          <td className="avg-cell">{formatEmissionValue(plant.avg_emissions)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="total-row">
                        <td>
                          <strong>GRAND TOTAL</strong>
                        </td>
                        <td className="text-center">
                          <strong>{aggregateData.total_scenarios}</strong>
                        </td>
                        <td className="emissions-cell">
                          <strong>{formatEmissionValue(aggregateData.grand_total_emissions)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_a1)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_a2)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_a3)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_a4_a5)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_b2_b5)}</strong>
                        </td>
                        <td>
                          <strong>{formatEmissionValue(aggregateData.grand_total_c1_c4)}</strong>
                        </td>
                        <td className="avg-cell">
                          <strong>{formatEmissionValue(aggregateData.avg_emissions_per_scenario)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AggregateDashboard;
